import React, { useState } from 'react';

type FontSize = 'normal' | 'grande' | 'maior';
type Contraste = 'normal' | 'alto';

/** Classes de tema do portal público, nos dois contrastes. */
export interface TemaPortal {
  alto: boolean;
  page: string;
  card: string;
  muted: string;
  input: string;
  primary: string;
  secondary: string;
  header: string;
  headerKicker: string;
  headerText: string;
  headerButton: string;
  tableHead: string;
  rowHover: string;
  divider: string;
  link: string;
  chip: string;
  font: string;
}

export const usePortalAcessibilidade = () => {
  const [fontSize, setFontSize] = useState<FontSize>('normal');
  const [contraste, setContraste] = useState<Contraste>('normal');
  const alto = contraste === 'alto';

  const secondary = alto
    ? 'border-yellow-600 text-yellow-300 hover:bg-gray-900'
    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50';

  const tema: TemaPortal = {
    alto,
    page: alto ? 'bg-black text-yellow-300' : 'bg-slate-50 text-slate-900',
    card: alto ? 'border border-yellow-600 bg-gray-950' : 'border border-slate-200 bg-white shadow-sm',
    muted: alto ? 'text-yellow-500' : 'text-slate-600',
    input: alto
      ? 'w-full rounded-md border-yellow-600 bg-gray-900 text-yellow-200 focus:border-yellow-400 focus:ring-yellow-400'
      : 'w-full rounded-md border-slate-300 bg-white text-slate-900 shadow-sm focus:border-blue-500 focus:ring-blue-500',
    primary: alto ? 'bg-yellow-400 text-black hover:bg-yellow-300' : 'bg-blue-700 text-white hover:bg-blue-800',
    secondary,
    header: alto ? 'border-b-2 border-yellow-400 bg-gray-950' : 'bg-blue-800 text-white',
    headerKicker: alto ? 'text-yellow-500' : 'text-blue-200',
    headerText: alto ? 'text-yellow-400' : 'text-blue-100',
    headerButton: alto ? secondary : 'border-white text-white hover:bg-blue-700',
    tableHead: alto ? 'bg-gray-900 text-yellow-300' : 'bg-slate-50 text-slate-700',
    rowHover: alto ? 'hover:bg-gray-900' : 'hover:bg-slate-50',
    divider: alto ? 'border-yellow-800 divide-gray-800' : 'border-slate-200 divide-slate-100',
    link: alto ? 'text-yellow-300 hover:text-yellow-100' : 'text-blue-700 hover:text-blue-900',
    chip: alto ? 'bg-gray-900 text-yellow-300' : 'bg-slate-100 text-slate-700',
    font: fontSize === 'maior' ? 'text-xl' : fontSize === 'grande' ? 'text-lg' : 'text-base',
  };

  const barra = (
    <div className={alto ? 'border-b border-yellow-800 bg-gray-950' : 'border-b border-slate-200 bg-slate-100'}>
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-1.5 sm:px-6 lg:px-8">
        <span className={`text-xs font-medium ${tema.muted}`}>Acessibilidade:</span>
        {(['normal', 'grande', 'maior'] as FontSize[]).map((size, index) => (
          <button
            key={size}
            type="button"
            onClick={() => setFontSize(size)}
            aria-pressed={fontSize === size}
            className={`rounded border px-2 py-0.5 text-xs ${fontSize === size ? tema.primary : tema.secondary}`}
          >
            {index === 0 ? 'A' : `A${'+'.repeat(index)}`}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setContraste(value => (value === 'normal' ? 'alto' : 'normal'))}
          aria-pressed={alto}
          className={`ml-2 rounded border px-2 py-0.5 text-xs ${alto ? tema.primary : tema.secondary}`}
        >
          {alto ? 'Contraste normal' : 'Alto contraste'}
        </button>
      </div>
    </div>
  );

  return { tema, barra };
};

export const PortalAcessibilidadeShell: React.FC<{ tema: TemaPortal; barra: React.ReactNode; children: React.ReactNode }> = ({ tema, barra, children }) => (
  <div className={`flex min-h-screen flex-col ${tema.page} ${tema.font}`}>
    {barra}
    {children}
  </div>
);
